import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Edit, Trash2, Copy, Eye, BarChart2, MousePointer } from 'lucide-react';
import { adAPI, domainAPI } from '../../services/api';
import toast from 'react-hot-toast';
import '../Admin/Admin.css';

const AD_POSITIONS = {
  head_code: 'Head Code',
  below_header: 'Below Header',
  home_banner: 'Home Banner',
  home_middle: 'Home Middle',
  home_bottom: 'Home Bottom',
  category_top: 'Category Top',
  category_middle: 'Category Middle',
  novel_top: 'Novel Detail Top',
  novel_middle: 'Novel Detail Middle',
  novel_bottom: 'Novel Detail Bottom',
  chapter_top: 'Chapter Top',
  chapter_middle: 'Chapter Middle',
  chapter_bottom: 'Chapter Bottom',
  chapter_between: 'Between Paragraphs',
  sidebar: 'Sidebar',
  popup: 'Popup',
  floating: 'Floating',
  interstitial: 'Interstitial'
};

const AD_TYPES = {
  adsense: 'Google AdSense',
  gam: 'Google Ad Manager',
  custom_html: 'Custom HTML',
  custom_js: 'Custom JavaScript',
  image_banner: 'Image Banner',
  video: 'Video Ad',
  native: 'Native Ad',
  amazon: 'Amazon Associates',
  affiliate: 'Affiliate Code'
};

const AD_TYPE_COLORS = {
  adsense: '#4285f4',
  gam: '#34a853',
  custom_html: '#8b5cf6',
  custom_js: '#f59e0b',
  image_banner: '#ec4899',
  video: '#ef4444',
  native: '#06b6d4',
  amazon: '#ff9900',
  affiliate: '#10b981'
};

const AdList = () => {
  const [ads, setAds] = useState([]);
  const [domains, setDomains] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDomain, setSelectedDomain] = useState('');
  const [selectedPosition, setSelectedPosition] = useState('');
  const [selectedType, setSelectedType] = useState('');

  useEffect(() => {
    fetchDomains();
  }, []);

  useEffect(() => {
    fetchAds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDomain, selectedPosition, selectedType]);

  const fetchDomains = async () => {
    try {
      const response = await domainAPI.getAll();
      setDomains(response.data || []);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchAds = async () => {
    setIsLoading(true);
    try {
      const params = {};
      if (selectedDomain) params.domain = selectedDomain;
      if (selectedPosition) params.position = selectedPosition;
      
      const response = await adAPI.getAll(params);
      let filteredAds = response.data || [];
      
      if (selectedType) {
        filteredAds = filteredAds.filter(ad => ad.adType === selectedType);
      }
      
      setAds(filteredAds);
    } catch (error) {
      toast.error('Failed to load ads');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleActive = async (ad) => {
    try {
      await adAPI.update(ad._id, { isActive: !ad.isActive });
      toast.success(ad.isActive ? 'Ad disabled' : 'Ad enabled');
      fetchAds();
    } catch (error) {
      toast.error('Something went wrong');
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) return;
    
    try {
      await adAPI.delete(id);
      toast.success('Ad deleted');
      fetchAds();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleDuplicate = async (ad) => {
    const targetDomain = prompt('Enter target domain ID (leave empty for same domain):');
    
    try {
      await adAPI.duplicate(ad._id, { targetDomain: targetDomain || ad.domain._id });
      toast.success('Ad duplicated');
      fetchAds();
    } catch (error) {
      toast.error('Failed to duplicate');
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toString() || '0';
  };

  return (
    <div className="ad-management">
      <div className="admin-page-header">
        <h1>Ad Management</h1>
        <Link to="/admin/ads/new" className="btn-add">
          <Plus size={18} />
          Add New Ad
        </Link>
      </div>

      {/* Filters */}
      <div style={{ 
        display: 'flex', 
        gap: '16px', 
        marginBottom: '24px',
        flexWrap: 'wrap'
      }}>
        <select
          value={selectedDomain}
          onChange={(e) => setSelectedDomain(e.target.value)}
          style={{
            padding: '10px 16px',
            background: 'var(--bg-card)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-sm)',
            color: 'var(--text-primary)',
            minWidth: '200px'
          }}
        >
          <option value="">All Domains</option>
          {domains.map((domain) => (
            <option key={domain._id} value={domain._id}>
              {domain.domain}
            </option>
          ))}
        </select>

        <select
          value={selectedPosition}
          onChange={(e) => setSelectedPosition(e.target.value)}
          style={{
            padding: '10px 16px',
            background: 'var(--bg-card)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-sm)',
            color: 'var(--text-primary)',
            minWidth: '200px'
          }}
        >
          <option value="">All Positions</option>
          {Object.entries(AD_POSITIONS).map(([key, label]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>

        <select
          value={selectedType}
          onChange={(e) => setSelectedType(e.target.value)}
          style={{
            padding: '10px 16px',
            background: 'var(--bg-card)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-sm)',
            color: 'var(--text-primary)',
            minWidth: '200px'
          }}
        >
          <option value="">All Types</option>
          {Object.entries(AD_TYPES).map(([key, label]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <div className="admin-loading">
          <div className="loading-spinner"></div>
        </div>
      ) : ads.length > 0 ? (
        <div className="admin-table-wrapper">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Domain</th>
                <th>Position</th>
                <th>Type</th>
                <th>Stats</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {ads.map((ad) => (
                <tr key={ad._id} style={{ opacity: ad.isActive ? 1 : 0.6 }}>
                  <td>
                    <strong>{ad.name}</strong>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                      Priority: {ad.priority}
                    </div>
                  </td>
                  <td>
                    <span style={{
                      padding: '4px 8px',
                      background: 'var(--bg-dark)',
                      borderRadius: '4px',
                      fontSize: '0.85rem'
                    }}>
                      {ad.domain?.domain || '-'}
                    </span>
                  </td>
                  <td>{AD_POSITIONS[ad.position] || ad.position}</td>
                  <td>
                    <span style={{
                      padding: '4px 10px',
                      background: `${AD_TYPE_COLORS[ad.adType]}20`,
                      color: AD_TYPE_COLORS[ad.adType],
                      borderRadius: '20px',
                      fontSize: '0.8rem',
                      fontWeight: 500
                    }}>
                      {AD_TYPES[ad.adType] || ad.adType}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '16px', fontSize: '0.85rem' }}>
                      <span title="Impressions">
                        <Eye size={14} style={{ marginRight: '4px' }} />
                        {formatNumber(ad.stats?.impressions)}
                      </span>
                      <span title="Clicks">
                        <MousePointer size={14} style={{ marginRight: '4px' }} />
                        {formatNumber(ad.stats?.clicks)}
                      </span>
                      <span title="CTR" style={{ color: 'var(--primary)' }}>
                        {ad.stats?.impressions > 0 
                          ? ((ad.stats.clicks / ad.stats.impressions) * 100).toFixed(2) 
                          : '0'}%
                      </span>
                    </div>
                  </td>
                  <td>
                    <button
                      onClick={() => handleToggleActive(ad)}
                      style={{
                        padding: '6px 12px',
                        background: ad.isActive ? 'rgba(34, 197, 94, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                        color: ad.isActive ? '#22c55e' : '#ef4444',
                        border: 'none',
                        borderRadius: '20px',
                        fontSize: '0.8rem',
                        cursor: 'pointer'
                      }}
                    >
                      {ad.isActive ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td>
                    <div className="table-actions">
                      <Link to={`/admin/ads/edit/${ad._id}`} className="btn-edit" title="Edit">
                        <Edit size={16} />
                      </Link>
                      <button 
                        className="btn-edit" 
                        title="Duplicate"
                        onClick={() => handleDuplicate(ad)}
                      >
                        <Copy size={16} />
                      </button>
                      <button 
                        className="btn-delete"
                        title="Delete"
                        onClick={() => handleDelete(ad._id, ad.name)}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="admin-empty">
          <BarChart2 size={48} style={{ marginBottom: '16px', opacity: 0.3 }} />
          <p>No ads found</p>
          <Link to="/admin/ads/new" style={{ color: 'var(--primary)', marginTop: '12px' }}>
            Create your first ad →
          </Link>
        </div>
      )}
    </div>
  );
};

export default AdList;
